import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl,
  InputLabel, Select,   Stack, Divider, Alert,  IconButton, CircularProgress, Snackbar,
  
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PaidIcon from "@mui/icons-material/Paid";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import BuildIcon from "@mui/icons-material/Build";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import axiosApi from "../Utils/axiosApi";
import AbsenceDialog from "../HostelDetails/AbsenceDialog";
import BillFormDialog from "../hostelerPayment/BillFormDialog";
import RoomActionDialog from "../occupancy/components/RoomActionDialog";

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
const addDays = (value, days) => new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);
const fmt = (value, options) => value.toLocaleDateString(undefined, options);

const statusOf = (tenant) => {
  if (tenant.group === "food") return "food";
  const today = startOfDay(new Date()).getTime();
  if (startOfDay(tenant.startDate).getTime() > today) return "upcoming";
  if (tenant.endDate && startOfDay(tenant.endDate).getTime() < today) return "completed";
  return "active";
};
const isHostelerActive = (tenant) =>
  String(tenant?.activeHosteler ?? "")
    .trim()
    .toUpperCase() === "Y";
const statusStyle = {
  active: { bg: "#2563eb", border: "#1d4ed8", label: "Active stay" },
  upcoming: { bg: "#15803d", border: "#166534", label: "Upcoming" },
  completed: { bg: "#64748b", border: "#475569", label: "Completed" },
  food: { bg: "#d97706", border: "#b45309", label: "Food only" },
};


const getMonthlyFeeStatus = (tenant, monthDate) => {
  const fees = Array.isArray(tenant?.monthlyFeeStatusDTOs)
    ? tenant.monthlyFeeStatusDTOs
    : [];

  const billMonth = `${monthDate.getFullYear()}-${String(
    monthDate.getMonth() + 1
  ).padStart(2, "0")}`;

  const fee = fees.find(
    (item) => String(item.billMonth) === billMonth
  );

  if (!fee) return "unknown";

  switch (String(fee.paymentStatus ?? "").toUpperCase()) {
    case "COMPLETED":
      return "paid";
    case "PARTIALLY_COMPLETED":
      return "partial";
    case "NOT_STARTED":
      return "pending";
    default:
      return "unknown";
  }
};

const MonthlyFeeIndicators = ({ tenant, minDate, maxDate }) => {
  const months = [];
  const current = new Date(
    minDate.getFullYear(),
    minDate.getMonth(),
    1
  );
  const last = new Date(
    maxDate.getFullYear(),
    maxDate.getMonth(),
    1
  );

  while (current <= last) {
    months.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  const statusConfig = {
    paid: { label: "✓", color: "#2e7d32", text: "Paid" },
    partial: { label: "½", color: "#ed6c02", text: "Partially paid" },
    pending: { label: "!", color: "#d32f2f", text: "Pending" },
    unknown: { label: "–", color: "#9e9e9e", text: "No payment data" },
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        marginLeft: 6,
        flexShrink: 0,
      }}
    >
      {months.map((month) => {
        const status = getMonthlyFeeStatus(tenant, month);
        const config = statusConfig[status];

        return (
          <Tooltip
            key={`${month.getFullYear()}-${month.getMonth()}`}
            title={`${month.toLocaleString("en", {
              month: "short",
              year: "numeric",
            })}: ${config.text}`}
            arrow
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: 4,
                backgroundColor: `${config.color}18`,
                color: config.color,
                border: `1px solid ${config.color}66`,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {config.label}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
};
const getTickDates = (view, minDate, maxDate) => {
  const ticks = [];
  if (view === "monthly") {
    let cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    while (cursor < maxDate) {
      ticks.push({ date: cursor < minDate ? minDate : cursor, label: fmt(cursor, { day: "numeric", month: "short" }), major: true });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
  } else {
    const step = view === "weekly" || view === "monthly" ? 7 : 1;
    let cursor = startOfDay(minDate);
    while (cursor < maxDate) {
      ticks.push({
        date: cursor,
        label: view === "daily"
          ? fmt(cursor, { weekday: "short", day: "numeric" })
          : view === "monthly" ? String(cursor.getDate()) : fmt(cursor, { day: "numeric", month: "short" }),
        major: cursor.getDate() === 1 || cursor.getTime() === minDate.getTime(),
      });
      cursor = addDays(cursor, step);
    }
  }
  return ticks;
};

const GanttChart = ({
  tenantsOnly = [],
  minDate,
  maxDate,
  isMobile = false,
  setSelectedTenant,
  view = "monthly",
  onMoveTenant,
  roomOptionsSource = tenantsOnly,
  hostelId = "",
  hostelList = [],
  onTenantAction,
}) => {
  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [moveTenant, setMoveTenant] = useState(null);
  const [targetRoom, setTargetRoom] = useState("");
  const [hoveredKey, setHoveredKey] = useState(null);
  const [roomContext, setRoomContext] = useState(null);
  
    const [tenantActionContext, setTenantActionContext] = useState(null);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
const [selectedAbsenceHosteler, setSelectedAbsenceHosteler] = useState(null);
const [leaveRequests, setLeaveRequests] = useState([]);
const [billFormOpen, setBillFormOpen] = useState(false);
const [billHostelId, setBillHostelId] = useState(null);
const [billHostelerId, setBillHostelerId] = useState(null);


const [availableRooms, setAvailableRooms] = useState([]);
const [selectedRoomType, setSelectedRoomType] = useState("");
const [selectedRoomNumber, setSelectedRoomNumber] = useState("");
const [roomsLoading, setRoomsLoading] = useState(false);

const [roomDialogOpen, setRoomDialogOpen] = useState(false);
const [roomDialogAction, setRoomDialogAction] = useState("details");
const [selectedRoom, setSelectedRoom] = useState(null);
const [roomDetailsList, setRoomDetailsList] = useState([]);
const [roomDetailsLoading, setRoomDetailsLoading] = useState(false);
const [roomDetailsError, setRoomDetailsError] = useState("");
const [scheduleDetails, setScheduleDetails] = useState(null);
const [scheduleError, setScheduleError] = useState("");
  useEffect(() => {
    const element = chartRef.current;
    if (!element) return undefined;
    const updateWidth = () => setContainerWidth(Math.floor(element.getBoundingClientRect().width));
    updateWidth();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const totalDays = Math.max(1, (maxDate.getTime() - minDate.getTime()) / DAY_MS);
  const labelWidth = isMobile ? 128 : 164;
  // Daily view fits the available chart width instead of leaving unused space.
  // Weekly/monthly keep their denser, scrollable scale for longer date ranges.
  const timelineWidth = view === "daily"
    ? Math.max(420, (containerWidth || 1180) - labelWidth)
    : Math.max(760, Math.ceil(totalDays * (view === "weekly" ? 24 : 12)));
  const dayWidth = timelineWidth / totalDays;
  const ticks = useMemo(() => getTickDates(view, minDate, maxDate), [view, minDate, maxDate]);
  const todayPosition = ((startOfDay(new Date()).getTime() - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * 100;
  const showToday = todayPosition >= 0 && todayPosition <= 100;

  

  const getBar = (tenant) => {
    const rawStart = startOfDay(tenant.startDate).getTime();
    const rawEnd = tenant.endDate
      ? Math.min(startOfDay(tenant.endDate).getTime() + DAY_MS, maxDate.getTime())
      : maxDate.getTime();
    const clippedStart = Math.max(rawStart, minDate.getTime());
    const clippedEnd = Math.min(Math.max(rawEnd, clippedStart + DAY_MS), maxDate.getTime());
    const left = Math.max(0, ((clippedStart - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * 100);
    const width = Math.max(0.5, ((clippedEnd - clippedStart) / (maxDate.getTime() - minDate.getTime())) * 100);
    return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%` };
  };
const [banner, setBanner] = useState({
  open: false,
  message: "",
  severity: "error",
});

const showBanner = (message, severity = "error") => {
  setBanner({ open: true, message, severity });
};
  const openContextMenu = (event, tenant) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, tenant });
  };
  const closeContextMenu = () => setContextMenu(null);
  const closeRoomContext = () => setRoomContext(null);
  const openRoomContextMenu = (event, tenant) => {
    event.preventDefault();
    event.stopPropagation();
    setRoomContext({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, tenant });
  };
  const roomIdentity = (tenant) => ({
    hostelId: tenant?.hostelId ?? hostelId,
    roomType: tenant?.roomType || "",
    roomNumber: tenant?.roomNumber || "",
    roomId: tenant?.roomId ?? null,
    hostelName: tenant?.hostelName || "",
    roomStatus: tenant?.roomStatus || "",
    maintenanceStatus: tenant?.maintenanceStatus || "",
    cleaningStatus: tenant?.cleaningStatus || "",
  });
  
  const loadAvailableRooms = async () => {
  if (!hostelId) return;

  setRoomsLoading(true);

  try {
    const response = await axiosApi.post("/public/getHostelRoom", {
      hostelSearchCriteria: {
        hostelIds: [Number(hostelId)],
      },
    });

    const payload = response?.data;
    const rooms = Array.isArray(payload)
      ? payload
      : payload?.rooms || payload?.data || [];

    setAvailableRooms(
      rooms.filter((room) => room.roomType && room.roomNumber)
    );
  } catch (error) {
    console.error("Failed to load available rooms:", error);
    setAvailableRooms([]);
  } finally {
    setRoomsLoading(false);
  }
};



 // Common dialog opener
const openRoomActionDialog = (action, tenant) => {
  closeRoomContext();

  setSelectedRoom(tenant);
  setRoomDialogAction(action);
  setRoomDialogOpen(true);

  setRoomDetailsList([]);
  setRoomDetailsError("");
  setScheduleDetails(null);
  setScheduleError("");
};


const openRoomDetails = async (tenant) => {
  const identity = roomIdentity(tenant);

  openRoomActionDialog("details", tenant);
  setRoomDetailsLoading(true);

  try {
    const response = await axiosApi.post("/public/getHostelRoom", {
      hostelSearchCriteria: {
        hostelIds: [Number(identity.hostelId)],
        roomType: identity.roomType,
        roomNumber: identity.roomNumber,
      },
    });

    const data = Array.isArray(response?.data)
      ? response.data
      : response?.data?.rooms || response?.data?.data || [];

    const match = data.find((room) => {
      const roomNumbers = String(
        room?.roomNumber ?? room?.number ?? ""
      )
        .split(",")
        .map((number) => number.trim());

      return (
        String(room?.roomType ?? room?.type ?? "").trim() ===
          String(identity.roomType).trim() &&
        roomNumbers.includes(String(identity.roomNumber).trim())
      );
    });

    if (!match) {
      setRoomDetailsError(
        data.length > 0
          ? "Could not match this room by room type and room number."
          : "No room details found."
      );
      return;
    }

    setSelectedRoom({
      ...tenant,
      ...match,
      roomType: match.roomType ?? tenant.roomType,
      roomNumber: match.roomNumber ?? tenant.roomNumber,
    });

    setRoomDetailsList([match]);
  } catch (error) {
    console.error("Failed to load room details:", error);
    setRoomDetailsError("Could not load room details.");
  } finally {
    setRoomDetailsLoading(false);
  }
};
const openRoomSchedule = async (tenant) => {
  const identity = roomIdentity(tenant);

  openRoomActionDialog("schedule", tenant);

  if (!identity.hostelId || !identity.roomType || !identity.roomNumber) {
    setScheduleError("Hostel ID, Room Type, or Room Number is missing.");
    return;
  }

  setRoomDetailsLoading(true);

  try {
    // Enable this when the backend API is ready.
    /*
    const response = await axiosApi.post(
      "/hostel/getRoomSchedule",
      {
        hostelIds: [Number(identity.hostelId)],
        roomType: identity.roomType,
        roomNumber: identity.roomNumber,
      }
    );

    const schedule = response?.data ?? null;
    setScheduleDetails(schedule);
    */

    setScheduleError("Room schedule API is not connected yet.");
  } catch (error) {
    console.error("Failed to load room schedule:", error);
    setScheduleError("Could not load room schedule.");
  } finally {
    setRoomDetailsLoading(false);
  }
};
// Open Maintenance
const openRoomMaintenance = (tenant) => {
  openRoomActionDialog("maintenance", tenant);
};

// Open Cleaning
const openRoomCleaning = (tenant) => {
  openRoomActionDialog("cleaning", tenant);
};
 

const handleOpenRoomAction = (action) => {
  const tenant = roomContext?.tenant;
  if (!tenant) return;

  switch (action) {
    case "details":
      openRoomDetails(tenant);
      break;

    case "schedule":
      openRoomSchedule(tenant);
      break;

    case "maintenance":
      openRoomMaintenance(tenant);
      break;

    case "cleaning":
      openRoomCleaning(tenant);
      break;

    default:
      console.warn("Unknown room action:", action);
  }
};


const handleRoomActionSave = async ({
  action,
  room,
  roomDetails,
  taskDetails,
}) => {
  if (action === "details") {
    showBanner(
      "Room details update API is not connected yet.",
      "warning"
    );
    return;
  }

  if (action !== "maintenance" && action !== "cleaning") {
    return;
  }

  const identity = roomIdentity(room ?? selectedRoom);

  if (!identity.hostelId || !identity.roomType || !identity.roomNumber) {
    showBanner("Hostel ID, Room Type, or Room Number is missing.");
    return;
  }

  const payload = {
    hostelIds: [Number(identity.hostelId)],
    roomType: identity.roomType,
    roomNumber: identity.roomNumber,
  };

  try {
    setRoomDetailsLoading(true);

    if (action === "maintenance") {
      await axiosApi.post(
        "/hostel/markRoomForMaintenance",
        payload
      );

      showBanner("Room marked for maintenance successfully.", "success");
    } else {
      await axiosApi.post(
        "/hostel/markRoomForCleaning",
        payload
      );

      showBanner("Room marked for cleaning successfully.", "success");
    }

    setRoomDialogOpen(false);
  } catch (error) {
    console.error(`Failed to execute ${action}:`, error);

    showBanner(
      error?.response?.data?.message ||
        `Could not mark room for ${action}.`,
      "error"
    );
  } finally {
    setRoomDetailsLoading(false);
  }
};

  

  const runTenantAction = (action, tenantOverride = null) => {
    const tenant = tenantOverride || contextMenu?.tenant;
    closeContextMenu();
    if (!tenant) return;
    if (action === "view") setSelectedTenant(tenant);
    else if (onTenantAction) onTenantAction(action, tenant);
    else setTenantActionContext({ action, tenant });
  };

  // Fetch the latest hosteler record before opening the existing edit UI.
 const getHostelerApi = async (tenant) => {
  const response = await axiosApi.post("/hosteler/getHosteler", {
    hostelIds:
      tenant?.hostelId != null ? [Number(tenant.hostelId)] : [],
    hostelerIds:
      tenant?.hostelerId != null ? [Number(tenant.hostelerId)] : [],
    accommodationType: tenant?.accommodationType || undefined,
  });

  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.hostelers)) return payload.hostelers;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload && typeof payload === "object") return [payload];

  return [];
};

  // Dedicated menu handlers route actions into the existing tenant UI/callback.
  const handleViewTenant = () => runTenantAction("view");
  
const handleEditTenant = async () => {
  const tenant = contextMenu?.tenant;
  closeContextMenu();

  if (!tenant) return;

  try {
    const hostelerRecords = await getHostelerApi(tenant);

    const tenantId =
      tenant.hostelerId ??
      tenant.hostelerID ??
      tenant.id ??
      tenant.tenantId;

    const tenantName = String(
      tenant.tenantName ?? tenant.hostelerName ?? ""
    ).trim().toLowerCase();

    const matchedRecord =
      hostelerRecords.find((record) => {
        const recordId =
          record?.hostelerId ??
          record?.hostelerID ??
          record?.id ??
          record?.tenantId;

        return (
          tenantId != null &&
          recordId != null &&
          String(recordId) === String(tenantId)
        );
      }) ||
      hostelerRecords.find((record) => {
        const recordName = String(
          record?.hostelerName ??
          record?.hostlerFirstName ??
          record?.tenantName ??
          ""
        ).trim().toLowerCase();

        return tenantName !== "" && recordName === tenantName;
      });

    if (!matchedRecord) {
      setTenantActionContext({
        action: "edit",
        tenant,
        error: "No matching hosteler record was returned by getHosteler.",
      });
      return;
    }

    runTenantAction("edit", {
      ...tenant,
      ...matchedRecord,
      id:
        matchedRecord.id ??
        matchedRecord.hostelerId ??
        matchedRecord.hostelerID ??
        matchedRecord.tenantId ??
        tenantId,
      hostelerId:
        matchedRecord.hostelerId ??
        matchedRecord.hostelerID ??
        matchedRecord.id ??
        tenantId,
      hostelId: matchedRecord.hostelId ?? tenant.hostelId ?? hostelId,
    });
  } catch (error) {
    console.error("Failed to load hosteler for editing:", error);

    setTenantActionContext({
      action: "edit",
      tenant,
      error: "Could not load hosteler details. Please check the getHosteler API request.",
    });
  }
};


const handleMarkAbsent = async () => {
  const tenant = contextMenu?.tenant;
  closeContextMenu();

  if (!tenant) return;

  try {
    const hostelerRecords = await getHostelerApi(tenant);

    const tenantId =
      tenant.hostelerId ??
      tenant.hostelerID ??
      tenant.id ??
      tenant.tenantId;

    const tenantName = String(
      tenant.tenantName ?? tenant.hostelerName ?? ""
    ).trim().toLowerCase();

    const matchedHosteler =
      hostelerRecords.find((record) => {
        const recordId =
          record?.hostelerId ??
          record?.hostelerID ??
          record?.id ??
          record?.tenantId;

        return (
          tenantId != null &&
          recordId != null &&
          String(recordId) === String(tenantId)
        );
      }) ||
      hostelerRecords.find((record) => {
        const recordName = String(
          record?.hostelerName ??
          record?.hostlerFirstName ??
          record?.tenantName ??
          ""
        ).trim().toLowerCase();

        return tenantName !== "" && recordName === tenantName;
      }) ||
      (hostelerRecords.length === 1 ? hostelerRecords[0] : null);

    if (!matchedHosteler) {
      setTenantActionContext({
        action: "markAbsent",
        tenant,
        error:
          "Could not find the matching hosteler record. Please check the hosteler ID and name returned by the API.",
      });
      return;
    }

    const resolvedHostelerId =
      matchedHosteler.hostelerId ??
      matchedHosteler.hostelerID ??
      matchedHosteler.id ??
      matchedHosteler.tenantId;

    const resolvedHostelId =
      matchedHosteler.hostelId ??
      tenant.hostelId ??
      hostelId;

    if (
      resolvedHostelerId == null ||
      resolvedHostelId == null
    ) {
      setTenantActionContext({
        action: "markAbsent",
        tenant,
        error: "Hosteler ID or hostel ID is missing.",
      });
      return;
    }

    const response = await axiosApi.post(
      "/hostelerLeaveRequest/getHostelLeaveRequest",
      {
        hostelerIds: [resolvedHostelerId],
        hostelIds: [resolvedHostelId],
      }
    );

    const responseData = response?.data;
    const absences = Array.isArray(responseData)
      ? responseData
      : responseData?.data ?? responseData?.leaveRequestList ?? [];

    setSelectedAbsenceHosteler({
      ...matchedHosteler,
      id: resolvedHostelerId,
      hostelerId: resolvedHostelerId,
      hostelId: resolvedHostelId,
      hostelName:
        matchedHosteler.hostelName ??
        tenant.hostelName ??
        "",
      hostelerName:
        matchedHosteler.hostelerName ??
        matchedHosteler.hostlerFirstName ??
        tenant.hostelerName ??
        tenant.tenantName ??
        "",
    });

    setLeaveRequests(absences);
    setAbsenceDialogOpen(true);
  } catch (error) {
    console.error(
      "Failed to open absence dialog:",
      error.response?.data || error
    );

    setTenantActionContext({
      action: "markAbsent",
      tenant,
      error: "Failed to load absence records. Please try again.",
    });
  }
};


const handleGenerateBill = async () => {
  const tenant = contextMenu?.tenant;
  closeContextMenu();

  if (!tenant) return;

  const resolvedHostelId =
    tenant.hostelId ?? hostelId ?? null;

  if (resolvedHostelId == null) {
    console.error("Hostel ID is missing:", tenant);
    showBanner("Hostel ID is missing. Please refresh the occupancy data.");
    return;
  }

  try {
    const response = await axiosApi.post(
      "/hosteler/getHosteler",
      {
        hostelIds: [Number(resolvedHostelId)],
      }
    );

    const responseData = response?.data;

    const hostelerList = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.hostelerList)
          ? responseData.hostelerList
          : [];

    const tenantHostelerId =
      tenant.hostelerId ??
      tenant.hostelerID ??
      tenant.id ??
      tenant.tenantId ??
      null;

    const tenantName = String(
      tenant.hostelerName ??
      tenant.hostlerName ??
      tenant.tenantName ??
      tenant.name ??
      ""
    ).trim().toLowerCase();

    const matchedHosteler =
      hostelerList.find((record) => {
        const recordId =
          record?.hostelerId ??
          record?.hostelerID ??
          record?.id ??
          record?.tenantId ??
          record?.[0];

        return (
          tenantHostelerId != null &&
          recordId != null &&
          String(recordId) === String(tenantHostelerId)
        );
      }) ||
      hostelerList.find((record) => {
        const recordName = String(
          record?.hostelerName ??
          record?.hostlerName ??
          record?.tenantName ??
          [
            record?.hostelerFirstName ??
              record?.hostlerFirstName ??
              "",
            record?.hostelerLastName ??
              record?.hostlerLastName ??
              "",
          ].filter(Boolean).join(" ")
        ).trim().toLowerCase();

        return tenantName !== "" && recordName === tenantName;
      });

    if (!matchedHosteler) {
      console.error(
        "Could not match Gantt tenant to a hosteler record:",
        { tenant, hostelerList }
      );

      showBanner("Hosteler record not found for this tenant.");
      return;
    }

    const actualHostelerId =
      matchedHosteler.hostelerId ??
      matchedHosteler.hostelerID ??
      matchedHosteler.id ??
      matchedHosteler.tenantId ??
      matchedHosteler[0];

    if (actualHostelerId == null) {
      console.error("Hosteler ID is missing from matched record:", matchedHosteler);
      showBanner("Hosteler ID is missing. Please check the API response.");
      return;
    }

    setBillHostelId(resolvedHostelId);
    setBillHostelerId(actualHostelerId);
    setBillFormOpen(true);
  } catch (error) {
    console.error(
      "Failed to fetch hosteler records:",
      error.response?.data || error
    );

    showBanner("Unable to load hosteler details. Please try again.");
  }
};

  

 const startMove = async () => {
  const tenant = contextMenu?.tenant;
  closeContextMenu();

  if (!tenant || !["STAY_WITH_FOOD", "STAY_ONLY"].includes(
    tenant.accommodationType
  )) {
    return;
  }

  setMoveTenant(tenant);
  setSelectedRoomType("");
  setSelectedRoomNumber("");
  setTargetRoom("");
  setAvailableRooms([]);

  await loadAvailableRooms();
};

const availableRoomTypes = useMemo(
  () => [...new Set(availableRooms.map((room) => room.roomType))],
  [availableRooms]
);

const availableRoomNumbers = useMemo(() => {
  const seen = new Set();

  return availableRooms
    .filter((room) => room.roomType === selectedRoomType)
    .flatMap((room) =>
      String(room.roomNumber)
        .split(",")
        .map((number) => number.trim())
        .filter(Boolean)
    )
    .filter((number) => {
      if (seen.has(number)) return false;
      seen.add(number);
      return true;
    });
}, [availableRooms, selectedRoomType]);

  

  const roomHasConflict = (room) => {
    if (!moveTenant) return false;
    const start = moveTenant.startDate.getTime();
    const end = moveTenant.endDate ? moveTenant.endDate.getTime() + DAY_MS : Number.POSITIVE_INFINITY;
    return roomOptionsSource.some((other) => {
      if (other.uiKey === moveTenant.uiKey || other.group === "food") return false;
      if (`${other.roomType || "Room"}::${other.roomNumber || ""}` !== room.key) return false;
      const otherStart = other.startDate.getTime();
      const otherEnd = other.endDate ? other.endDate.getTime() + DAY_MS : Number.POSITIVE_INFINITY;
      return start < otherEnd && otherStart < end;
    });
  };
 const [movingTenant, setMovingTenant] = useState(false);

const confirmMove = async () => {
  if (!moveTenant || !selectedRoomType || !selectedRoomNumber) {
    return;
  }

  const hostelerId = moveTenant.hostelerId;

  if (hostelerId == null) {
    showBanner("Hosteler ID is missing.");
    return;
  }

  const destinationRoom = availableRooms.find((room) => {
    const type = room.roomType ?? room.type;

    const numbers = Array.isArray(room.roomNumbers)
      ? room.roomNumbers.map(String)
      : String(room.roomNumber ?? "")
          .split(",")
          .map((n) => n.trim());

    return (
      String(type) === String(selectedRoomType) &&
      numbers.includes(String(selectedRoomNumber))
    );
  });

  if (!destinationRoom) {
    showBanner("Selected destination room was not found.");
    return;
  }

  const payload = {
    hostelerDTO: {
      id: hostelerId,
      roomType: selectedRoomType,
      roomNumber: selectedRoomNumber,
    },
  };

  try {
    setMovingTenant(true);

    await axiosApi.post("/hosteler/updateHostelRoom", payload);

    await onMoveTenant(moveTenant, {
      ...destinationRoom,
      roomType: selectedRoomType,
      roomNumber: selectedRoomNumber,
    });

    setMoveTenant(null);
    setSelectedRoomType("");
    setSelectedRoomNumber("");
  } catch (error) {
    console.error("Failed to move tenant:", error);

    
  } finally {
    setMovingTenant(false);
  }
};

  const monthBands = useMemo(() => {
    const bands = [];
    let cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    while (cursor < maxDate) {
      const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const start = Math.max(cursor.getTime(), minDate.getTime());
      const end = Math.min(next.getTime(), maxDate.getTime());
      bands.push({
        label: fmt(cursor, { month: "long", year: "numeric" }),
        left: ((start - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * 100,
        width: ((end - start) / (maxDate.getTime() - minDate.getTime())) * 100,
      });
      cursor = next;
    }
    return bands;
  }, [minDate, maxDate]);

  const gridWidth = labelWidth + timelineWidth;

  return (
    <Box
  ref={chartRef}
  sx={{
    width: "100%",
    minWidth: 0,
    maxHeight: "78vh",
    color: "#0f172a",
    fontSize: 13,
    overflowX: "auto",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-x pan-y",
    borderRadius: 2,
    position: "relative",
    isolation: "isolate",
  }}
>
      <Box sx={{ width: gridWidth, minWidth: "100%" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: `${labelWidth}px ${timelineWidth}px`, width: gridWidth, borderBottom: "1px solid #cbd5e1", bgcolor: "#f8fafc", position: "sticky", top: 0, zIndex: 6 }}>
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", borderRight: "1px solid #e2e8f0", fontWeight: 800, color: "#334155", position: "sticky", left: 0, zIndex: 5, bgcolor: "#f8fafc" }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: "#64748b" }}>Room / status</Typography>
          </Box>
          <Box sx={{ overflow: "hidden" }}>
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Box sx={{ position: "relative", height: 28, borderBottom: "1px solid #e2e8f0", bgcolor: "#f1f5f9" }}>
                {monthBands.map((band, index) => (
                  <Box key={`${band.label}-${index}`} sx={{ position: "absolute", left: `${band.left}%`, width: `${band.width}%`, height: "100%", px: 1, display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap", borderRight: "1px solid #dbe3ed", fontSize: 11, fontWeight: 800, color: "#475569" }}>{band.label}</Box>
                ))}
              </Box>
              <Box sx={{ height: 34, position: "relative", bgcolor: "#fff" }}>
                {ticks.map((tick, index) => {
                  const left = ((tick.date.getTime() - minDate.getTime()) / (maxDate.getTime() - minDate.getTime())) * 100;
const nextTick = ticks[index + 1];

const tickWidth = nextTick
  ? ((nextTick.date.getTime() - tick.date.getTime()) /
      (maxDate.getTime() - minDate.getTime())) *
    timelineWidth
  : timelineWidth -
    ((tick.date.getTime() - minDate.getTime()) /
      (maxDate.getTime() - minDate.getTime())) *
      timelineWidth;

return (
  <Box
    key={`${tick.date.toISOString()}-${index}`}
    sx={{
      position: "absolute",
      left: `${left}%`,
      width: `${Math.max(0, tickWidth)}px`,
      height: "100%",
      borderLeft: "1px solid #e2e8f0",
      pl: 0.6,
      pt: 0.7,
      boxSizing: "border-box",
      color: tick.major ? "#0f172a" : "#64748b",
      fontSize: { xs: 10, sm: 10.5 },
      fontWeight: tick.major ? 800 : 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "clip",
    }}
  >
    {tick.label}
  </Box>
);
                })}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: gridWidth }}>
          {tenantsOnly.map((tenant, index) => {
           const status = statusOf(tenant);
const activeHosteler = isHostelerActive(tenant);

const style = activeHosteler
  ? statusStyle[status]
  : {
      bg: "#9ca3af",
      border: "#6b7280",
      label: "Inactive hosteler",
    };
            
            const bar = getBar(tenant);
            const roomLabel = tenant.group === "food" ? "Food service" : `${tenant.roomType || "Room"} · ${tenant.roomNumber || "—"}`;
			const paymentLabel = (() => {
  const fees = tenant.monthlyFeeStatusDTOs || [];

  if (!fees.length) return "Monthly fee status unavailable";

  return fees
    .map((fee) => {
      const status = String(fee.paymentStatus || "").toUpperCase();

      const label =
        status === "COMPLETED"
          ? "Paid"
          : status === "PARTIALLY_COMPLETED"
            ? "Partially paid"
            : status === "NOT_STARTED"
              ? "Pending"
              : "Unknown";

      return `${fee.billMonth}: ${label}`;
    })
    .join(" | ");
})();
              return (
              <Box key={tenant.uiKey || `${tenant.yLabel}-${index}`} sx={{ display: "grid", gridTemplateColumns: `${labelWidth}px ${timelineWidth}px`, width: gridWidth, minHeight: 46, borderBottom: "1px solid #edf2f7", bgcolor: index % 2 ? "#fbfdff" : "#fff", "&:hover": { bgcolor: "#f8fafc" } }}>
                <Box
                  sx={{
                    px: 1, py: 0.5, borderRight: "1px solid #e2e8f0",
                    display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center", columnGap: 0.5, minWidth: 0,
                    width: `${labelWidth}px`, boxSizing: "border-box",
                    position: "sticky", left: 0, zIndex: 4,
                    bgcolor: index % 2 ? "#fbfdff" : "#fff", cursor: "default",
                    visibility: "visible !important", opacity: "1 !important",
                    overflow: "visible",
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      gridColumn: "1", minWidth: 0, width: "100%",
                      display: "flex !important", flexDirection: "column",
                      justifyContent: "center", gap: "2px",
                      visibility: "visible !important", opacity: "1 !important",
                      overflow: "hidden", position: "relative",
                    }}
                  >
                    <Box
                      component="div"
                      title={tenant.group === "food" ? "Food service" : (tenant.roomType || "Room")}
                      sx={{
                        display: "block !important", width: "100%", minWidth: 0,
                        color: "#334155 !important", fontSize: "11px !important",
                        fontWeight: "750 !important", lineHeight: "14px !important",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        visibility: "visible !important", opacity: "1 !important",
                      }}
                    >
                      {tenant.group === "food" ? "Food service" : (tenant.roomType || "Room")}
                    </Box>
                    <Box
                      component="div"
                      title={tenant.group === "food" ? "Food only" : `Room ${tenant.roomNumber || "—"}`}
                      sx={{
                        display: "block !important", width: "100%", minWidth: 0,
                        color: "#0f172a !important", fontSize: "12px !important",
                        fontWeight: "850 !important", lineHeight: "15px !important",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        visibility: "visible !important", opacity: "1 !important",
                      }}
                    >
                      {tenant.group === "food" ? "Food only" : `Room ${tenant.roomNumber || "—"}`}
                    </Box>
                  </Box>
                  {tenant.group !== "food" && (tenant.isUnderMaintenance || String(tenant.roomStatus || tenant.maintenanceStatus || "").toUpperCase().includes("MAINTENANCE")) && <Tooltip title="Room marked for maintenance"><WarningAmberIcon sx={{ fontSize: 15, color: "#d97706", flexShrink: 0 }} /></Tooltip>}
                  {tenant.group !== "food" && (tenant.needsCleaning || tenant.isCleaningRequired || String(tenant.roomStatus || tenant.cleaningStatus || "").toUpperCase().includes("CLEAN")) && <Tooltip title="Room cleaning required"><CheckroomIcon sx={{ fontSize: 15, color: "#0284c7", flexShrink: 0 }} /></Tooltip>}
                  {tenant.group !== "food" && <Tooltip title={`Room actions · ${roomLabel}`}><IconButton size="small" aria-label={`Room actions ${roomLabel}`} onClick={(event) => { event.preventDefault(); event.stopPropagation(); openRoomContextMenu(event, tenant); }} sx={{ p: 0.35, flex: "0 0 auto", color: "#475569" }}><MoreVertIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
                </Box>
                <Box sx={{ overflow: "hidden" }}>
                  <Box sx={{ width: timelineWidth, height: 46, position: "relative", backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent ${Math.max(1, dayWidth - 1)}px, #eef2f7 ${Math.max(1, dayWidth - 1)}px, #eef2f7 ${dayWidth}px)` }}>
                    {showToday && <Box sx={{ position: "absolute", zIndex: 1, left: `${todayPosition}%`, top: 0, bottom: 0, borderLeft: "2px solid #ef4444", opacity: 0.75, pointerEvents: "none" }} />}
                    <Tooltip title={<Box><Typography variant="body2" fontWeight={800}>{tenant.tenantName || "Tenant"}</Typography><Typography variant="caption">{fmt(tenant.startDate, { day: "numeric", month: "short", year: "numeric" })} – {tenant.endDate ? fmt(tenant.endDate, { day: "numeric", month: "short", year: "numeric" }) : "Ongoing"}</Typography><br /><Typography variant="caption">{style.label} · {paymentLabel}</Typography></Box>}>
                      <Box
                        role="button" tabIndex={0}
                        onClick={() => setSelectedTenant(tenant)}
                        onContextMenu={(event) => openContextMenu(event, tenant)}
                        onMouseEnter={() => setHoveredKey(tenant.uiKey)}
                        onMouseLeave={() => setHoveredKey(null)}
                        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelectedTenant(tenant); }}
                        sx={{ position: "absolute", zIndex: hoveredKey === tenant.uiKey ? 3 : 2, top: 10, left: bar.left, width: bar.width, minWidth: view === "monthly" ? 34 : 44, height: 26, borderRadius: 1.5, bgcolor: style.bg, border: `1px solid ${style.border}`, boxShadow: hoveredKey === tenant.uiKey ? "0 4px 12px rgba(15,23,42,.22)" : "0 1px 2px rgba(15,23,42,.12)", display: "flex", alignItems: "center", gap: 0.5, px: 0.75, boxSizing: "border-box", color: "#fff", overflow: "hidden", whiteSpace: "nowrap", cursor: "pointer", transition: "box-shadow .15s, filter .15s", filter: hoveredKey === tenant.uiKey ? "brightness(1.06)" : "none", outline: "none", "&:focus-visible": { boxShadow: "0 0 0 3px #93c5fd" } }}
                      >
					  {(tenant.isAbsent === true ||
  tenant.absent === true ||
  String(
    tenant.attendanceStatus || tenant.presenceStatus || ""
  ).toUpperCase() === "ABSENT") && (
  <Tooltip title="Guest marked absent">
    <PersonOffIcon sx={{ fontSize: 14, flexShrink: 0 }} />
  </Tooltip>
)}

<MonthlyFeeIndicators
  tenant={tenant}
  minDate={minDate}
  maxDate={maxDate}
/>

<Typography
  noWrap
  sx={{
    fontSize: 10,
    fontWeight: 750,
    color: "inherit",
    textShadow: "0 1px 1px rgba(0,0,0,.15)",
    textAlign: "left",
  }}
>
  {tenant.tenantName || "Tenant"}
</Typography>

 </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", px: 1, py: 0.9, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", minWidth: gridWidth, boxSizing: "border-box", alignItems: "center" }}>
        {Object.entries(statusStyle).map(([key, item]) => <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.7 }}><Box sx={{ width: 9, height: 9, borderRadius: 0.7, bgcolor: item.bg }} /><Typography sx={{ fontSize: 11, color: "#64748b" }}>{item.label}</Typography></Box>)}
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><PaidIcon sx={{ fontSize: 14, color: "#15803d" }} /><Typography sx={{ fontSize: 11, color: "#64748b" }}>Fees paid</Typography></Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><MoneyOffIcon sx={{ fontSize: 14, color: "#b91c1c" }} /><Typography sx={{ fontSize: 11, color: "#64748b" }}>Fees pending</Typography></Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><HelpOutlineIcon sx={{ fontSize: 14, color: "#64748b" }} /><Typography sx={{ fontSize: 11, color: "#64748b" }}>Fee status unknown</Typography></Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><EventAvailableIcon sx={{ fontSize: 14, color: "#ef4444" }} /><Typography sx={{ fontSize: 11, color: "#64748b" }}>Today</Typography></Box>
      </Box>

    
<Menu
  open={Boolean(contextMenu)}
  onClose={closeContextMenu}
  anchorReference="anchorPosition"
  anchorPosition={
    contextMenu
      ? {
          top: contextMenu.mouseY,
          left: contextMenu.mouseX,
        }
      : undefined
  }
>
  {/* Available for active and inactive hostellers */}
  <MenuItem onClick={handleViewTenant}>
    <ListItemIcon>
      <VisibilityIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="View tenant details" />
  </MenuItem>

  {/* Actions available only for active hostellers */}
  {isHostelerActive(contextMenu?.tenant) && (
    <>
      <MenuItem onClick={handleEditTenant}>
        <ListItemIcon>
          <EditNoteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Edit tenant / stay" />
      </MenuItem>

      <MenuItem onClick={handleMarkAbsent}>
        <ListItemIcon>
          <PersonOffIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Leave / Absence" />
      </MenuItem>

      {["STAY_WITH_FOOD", "STAY_ONLY"].includes(
        contextMenu?.tenant?.accommodationType
      ) && (
        <MenuItem onClick={startMove}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Move to another room" />
        </MenuItem>
      )}

      <Divider />

      <MenuItem onClick={handleGenerateBill}>
        <ListItemIcon>
          <ReceiptLongIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Generate bill" />
      </MenuItem>

      
    </>
  )}
</Menu>

<Menu
  open={Boolean(roomContext)}
  onClose={closeRoomContext}
  anchorReference="anchorPosition"
  anchorPosition={
    roomContext
      ? {
          top: roomContext.mouseY,
          left: roomContext.mouseX,
        }
      : undefined
  }
>
  <MenuItem onClick={() => handleOpenRoomAction("details")}>
    <ListItemIcon>
      <MeetingRoomIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Room details" />
  </MenuItem>

  <MenuItem onClick={() => handleOpenRoomAction("schedule")}>
    <ListItemIcon>
      <EventAvailableIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Room schedule" />
  </MenuItem>

  <Divider />

  <MenuItem onClick={() => handleOpenRoomAction("maintenance")}>
    <ListItemIcon>
      <BuildIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Mark for maintenance" />
  </MenuItem>

  <MenuItem onClick={() => handleOpenRoomAction("cleaning")}>
    <ListItemIcon>
      <CleaningServicesIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary="Mark for cleaning" />
  </MenuItem>
</Menu>


    
      <Dialog open={Boolean(tenantActionContext)} onClose={() => setTenantActionContext(null)} fullWidth maxWidth="xs">
        <DialogTitle>
  {tenantActionContext?.action === "edit"
    ? "Edit tenant / stay"
    : tenantActionContext?.action === "markAbsent"
      ? "Mark absent"
      : tenantActionContext?.action === "generateBill"
        ? "Generate bill"
        : "View bills"}
</DialogTitle>
        <DialogContent>
          {tenantActionContext?.error
            ? <Alert severity="error" sx={{ mt: 1 }}>{tenantActionContext.error}</Alert>
            : <Alert severity="warning" sx={{ mt: 1 }}>This action needs to be connected to your existing tenant editor or billing component. No tenant or bill data has been changed.</Alert>}
          <Typography variant="body2" sx={{ mt: 1 }}>{tenantActionContext?.tenant?.tenantName || tenantActionContext?.tenant?.hostelerName || "Tenant"} · {tenantActionContext?.tenant?.accommodationType || "Accommodation not specified"}</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setTenantActionContext(null)}>Close</Button></DialogActions>
      </Dialog>

      
<Dialog
  open={Boolean(moveTenant)}
  onClose={() => setMoveTenant(null)}
  fullWidth
  maxWidth="xs"
  PaperProps={{
    sx: {
      borderRadius: 2,
      overflow: "hidden",
    },
  }}
>
  <DialogTitle sx={{ pb: 1.5 }}>
  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
    Move tenant to another room
  </Typography>

  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
    Transfer the tenant to a different room.
  </Typography>

  <Box
    sx={{
      mt: 1.5,
      p: 1.5,
      borderRadius: 2,
      border: "1px solid #e2e8f0",
      bgcolor: "#f8fafc",
      display: "flex",
      alignItems: "center",
      width: "100%",
      boxSizing: "border-box",
      gap: 2,
    }}
  >
    {/* Tenant */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        TENANT
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "#0f172a",
          mt: 0.4,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {moveTenant?.tenantName || moveTenant?.hostelerName || "Tenant"}
      </Typography>
    </Box>

    <Divider orientation="vertical" flexItem />

    {/* Current Room */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        CURRENT ROOM
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "#334155",
          mt: 0.4,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {moveTenant?.roomType || "Room"} · {moveTenant?.roomNumber || "—"}
      </Typography>
    </Box>
  </Box>
</DialogTitle>

  <DialogContent sx={{ pt: "12px !important", pb: 2 }}>
    <Stack spacing={2}>
      <FormControl fullWidth size="small">
        <InputLabel id="move-room-type-label">
          Room type
        </InputLabel>
        <Select
          labelId="move-room-type-label"
          label="Room type"
          value={selectedRoomType}
          onChange={(event) => {
            setSelectedRoomType(event.target.value);
            setSelectedRoomNumber("");
            setTargetRoom("");
          }}
          disabled={roomsLoading}
        >
          {availableRoomTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="move-room-number-label">
          Room number
        </InputLabel>
        <Select
          labelId="move-room-number-label"
          label="Room number"
          value={selectedRoomNumber}
          onChange={(event) => {
            const number = event.target.value;
            setSelectedRoomNumber(number);
            setTargetRoom(`${selectedRoomType}::${number}`);
          }}
          disabled={!selectedRoomType || roomsLoading}
        >
          {availableRoomNumbers.map((number) => {
            const room = {
              key: `${selectedRoomType}::${number}`,
            };

            const conflict = roomHasConflict(room);

            const sameRoom =
              selectedRoomType === (moveTenant?.roomType || "Room") &&
              String(number) ===
                String(moveTenant?.roomNumber || "");

            return (
              <MenuItem
                key={number}
                value={number}
                disabled={sameRoom}
                sx={{
                  ...(conflict && {
                    color: "#9e9e9e",
                    backgroundColor: "#eeeeee",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }),
                }}
              >
                {number}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Compact booking-conflict legend */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          backgroundColor: "action.hover",
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: "2px",
            backgroundColor: "#eeeeee",
            border: "1px solid #bdbdbd",
          }}
        />
        <Typography variant="caption" color="text.secondary">
          Grey indicates a booking conflict. Selection is allowed.
        </Typography>
      </Box>

      {roomsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!roomsLoading && availableRooms.length === 0 && (
        <Alert severity="warning" sx={{ py: 0 }}>
          No room details found for this property.
        </Alert>
      )}
    </Stack>
  </DialogContent>

  <DialogActions
    sx={{
      px: 2.5,
      py: 1.5,
      borderTop: 1,
      borderColor: "divider",
      gap: 1,
    }}
  >
    <Button
      onClick={() => setMoveTenant(null)}
      color="inherit"
    >
      Cancel
    </Button>

    <Button
  variant="contained"
  onClick={confirmMove}
  disabled={
    movingTenant ||
    !selectedRoomType ||
    !selectedRoomNumber ||
    !targetRoom ||
    roomsLoading
  }
>
  {movingTenant ? (
    <>
      <CircularProgress
        size={16}
        color="inherit"
        sx={{ mr: 1 }}
      />
      Moving...
    </>
  ) : (
    "Move tenant"
  )}
</Button>
  </DialogActions>
</Dialog>

	  <AbsenceDialog
  open={absenceDialogOpen}
  onClose={() => {
    setAbsenceDialogOpen(false);
    setSelectedAbsenceHosteler(null);
    setLeaveRequests([]);
  }}
  absences={leaveRequests}
  hostelId={
    selectedAbsenceHosteler?.hostelId ??
    selectedAbsenceHosteler?.hostel?.id
  }
  hostelName={
    selectedAbsenceHosteler?.hostelName ??
    selectedAbsenceHosteler?.hostel?.hostelName ??
    ""
  }
  hostelerId={
    selectedAbsenceHosteler?.hostelerId ??
    selectedAbsenceHosteler?.hostelerID ??
    selectedAbsenceHosteler?.id
  }
  hostelerName={
    selectedAbsenceHosteler?.hostelerName ??
    selectedAbsenceHosteler?.tenantName ??
    ""
  }
/>

<BillFormDialog
  open={billFormOpen}
  onClose={() => {
    setBillFormOpen(false);
    setBillHostelId(null);
    setBillHostelerId(null);
  }}
  hostelList={hostelList}
  initialHostelId={billHostelId}
  initialHostelerId={billHostelerId}
  fixedSelection={true}
/>
<RoomActionDialog
  open={roomDialogOpen}
  onClose={() => {
    setRoomDialogOpen(false);
    setRoomDetailsError("");
    setScheduleError("");
  }}
  action={roomDialogAction}
  room={selectedRoom}
  roomDetailsList={roomDetailsList}
  selectedRoomIndex={0}
  scheduleDetails={scheduleDetails}
  scheduleError={scheduleError}
  loading={roomDetailsLoading}
  detailsError={roomDetailsError}
  onSave={handleRoomActionSave}
/>
<Snackbar
  open={banner.open}
  autoHideDuration={4000}
  onClose={() =>
    setBanner((prev) => ({ ...prev, open: false }))
  }
  anchorOrigin={{ vertical: "top", horizontal: "right" }}
>
  <Alert
    onClose={() =>
      setBanner((prev) => ({ ...prev, open: false }))
    }
    severity={banner.severity}
    variant="filled"
    sx={{ width: "100%" }}
  >
    {banner.message}
  </Alert>
</Snackbar>

    </Box>
  );
};

export default GanttChart;
